import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

// Loading component
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
    <div className="text-white text-xl">Loading...</div>
  </div>
);

// Eager load authentication pages (needed immediately)
import SignIn from './pages/Auth/SignIn';
import BetaSignup from './pages/Auth/BetaSignup';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import AcceptInvitation from './pages/Auth/AcceptInvitation';

// Lazy load all other pages (loaded on-demand)
const ClientDashboard = lazy(() => import('./components/ClientDashboard'));
const URLAnalyzerCampaignPage = lazy(() => import('./components/URLAnalyzerCampaignPage'));
const AnalysisHistory = lazy(() => import('./components/AnalysisHistory'));
const AnalysisResults = lazy(() => import('./components/AnalysisResults'));
const AIOResults = lazy(() => import('./components/AIOResults'));
const GeneralAnalysis = lazy(() => import('./components/GeneralAnalysis'));
const PageDetails = lazy(() => import('./components/PageDetails'));
const AIOPageDetails = lazy(() => import('./components/AIOPageDetails'));
const CompetitiveAnalysis = lazy(() => import('./components/CompetitiveAnalysis'));
const CompetitiveAnalysisDetails = lazy(() => import('./components/CompetitiveAnalysisDetails'));
const ScoringProfiles = lazy(() => import('./components/ScoringProfiles'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const AccountSettings = lazy(() => import('./components/AccountSettings'));
const BillingUsage = lazy(() => import('./components/BillingUsage'));
const PageVault = lazy(() => import('./components/PageVault'));
const Reports = lazy(() => import('./components/Reports'));
const AIOReportView = lazy(() => import('./components/AIOReportView'));
const PageFAQs = lazy(() => import('./components/PageFAQs'));
const PageLevelSchema = lazy(() => import('./components/PageLevelSchema'));
const LlmsManager = lazy(() => import('./components/LlmsManager'));
const WordPressPlugin = lazy(() => import('./components/WordPressPlugin'));
const WordPressPluginLanding = lazy(() => import('./components/WordPressPluginLanding').then(module => ({ default: module.WordPressPluginLanding })));
const RemoveContent = lazy(() => import('./components/RemoveContent'));
const LeadsPage = lazy(() => import('./components/LeadsPage'));
const LeadsSettingsPage = lazy(() => import('./components/LeadsSettingsPage'));

// Knowledgebase articles
const GettingStartedArticle = lazy(() => import('./components/kb/GettingStartedArticle'));
const SEOScoresArticle = lazy(() => import('./components/kb/SEOScoresArticle'));
const AIOOptimizationArticle = lazy(() => import('./components/kb/AIOOptimizationArticle'));
const TechnicalSEOArticle = lazy(() => import('./components/kb/TechnicalSEOArticle'));
const SchemaMarkupArticle = lazy(() => import('./components/kb/SchemaMarkupArticle'));
const ClientReportingArticle = lazy(() => import('./components/kb/ClientReportingArticle'));

// Admin pages (loaded on-demand only for admins)
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage'));
const TenantsPage = lazy(() => import('./pages/admin/TenantsPage'));
const ProjectsPage = lazy(() => import('./pages/admin/ProjectsPage'));
const SuperAdminsPage = lazy(() => import('./pages/admin/SuperAdminsPage'));
const SystemAlertsPage = lazy(() => import('./pages/admin/SystemAlertsPage'));
const PerformancePage = lazy(() => import('./pages/admin/PerformancePage'));
const RevenuePage = lazy(() => import('./pages/admin/RevenuePage'));
const ProductsPage = lazy(() => import('./pages/admin/ProductsPage'));
const SEOScoringPage = lazy(() => import('./pages/admin/SEOScoringPage'));
const GeneralAnalysisScoringPage = lazy(() => import('./pages/admin/GeneralAnalysisScoringPage'));
const SystemServicesPage = lazy(() => import('./pages/admin/SystemServicesPage'));
const GeneralSettingsPage = lazy(() => import('./pages/admin/GeneralSettingsPage'));

function App() {
  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Authentication Routes */}
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<BetaSignup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />

          {/* Public Routes */}
          <Route path="/wordpress-plugin" element={<WordPressPluginLanding />} />

          {/* Protected Client Routes */}
          <Route path="/" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
        <Route path="/campaign" element={<ProtectedRoute><URLAnalyzerCampaignPage /></ProtectedRoute>} />
        <Route path="/analysis/:websiteId" element={<ProtectedRoute><URLAnalyzerCampaignPage /></ProtectedRoute>} />
        <Route path="/page-vault/:websiteId" element={<ProtectedRoute><PageVault /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><AnalysisHistory /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/results" element={<ProtectedRoute><AnalysisResults /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/aio-results" element={<ProtectedRoute><AIOResults /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/general-analysis" element={<ProtectedRoute><GeneralAnalysis /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/reports/:reportId" element={<ProtectedRoute><AIOReportView /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/faqs" element={<ProtectedRoute><PageFAQs /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/schema" element={<ProtectedRoute><PageLevelSchema /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/llms-manager" element={<ProtectedRoute><LlmsManager /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/competitors" element={<ProtectedRoute><CompetitiveAnalysis /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/competitors/:analysisId" element={<ProtectedRoute><CompetitiveAnalysisDetails /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/pages/:pageId" element={<ProtectedRoute><PageDetails /></ProtectedRoute>} />
        <Route path="/websites/:websiteId/pages/:pageId/aio" element={<ProtectedRoute><AIOPageDetails /></ProtectedRoute>} />
        <Route path="/scoring-profiles" element={<ProtectedRoute><ScoringProfiles /></ProtectedRoute>} />
        <Route path="/profile-settings" element={<Navigate to="/account-settings" replace />} />
        <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingUsage /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
        <Route path="/wordpress-plugin-instructions" element={<ProtectedRoute><WordPressPlugin /></ProtectedRoute>} />
        <Route path="/remove-content" element={<ProtectedRoute><RemoveContent /></ProtectedRoute>} />
        <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
        <Route path="/leads-settings" element={<ProtectedRoute><LeadsSettingsPage /></ProtectedRoute>} />

        {/* Knowledgebase Articles */}
        <Route path="/kb/getting-started" element={<ProtectedRoute><GettingStartedArticle /></ProtectedRoute>} />
        <Route path="/kb/seo-scores" element={<ProtectedRoute><SEOScoresArticle /></ProtectedRoute>} />
        <Route path="/kb/aio-best-practices" element={<ProtectedRoute><AIOOptimizationArticle /></ProtectedRoute>} />
        <Route path="/kb/technical-seo" element={<ProtectedRoute><TechnicalSEOArticle /></ProtectedRoute>} />
        <Route path="/kb/schema-markup" element={<ProtectedRoute><SchemaMarkupArticle /></ProtectedRoute>} />
        <Route path="/kb/client-reporting" element={<ProtectedRoute><ClientReportingArticle /></ProtectedRoute>} />

        {/* Protected Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminOverviewPage /></ProtectedRoute>} />
        <Route path="/admin/tenants" element={<ProtectedRoute requiredRole="admin"><TenantsPage /></ProtectedRoute>} />
        <Route path="/admin/projects" element={<ProtectedRoute requiredRole="admin"><ProjectsPage /></ProtectedRoute>} />
        <Route path="/admin/super-admins" element={<ProtectedRoute requiredRole="super_admin"><SuperAdminsPage /></ProtectedRoute>} />
        <Route path="/admin/system-alerts" element={<ProtectedRoute requiredRole="admin"><SystemAlertsPage /></ProtectedRoute>} />
        <Route path="/admin/performance" element={<ProtectedRoute requiredRole="admin"><PerformancePage /></ProtectedRoute>} />
        <Route path="/admin/revenue" element={<ProtectedRoute requiredRole="admin"><RevenuePage /></ProtectedRoute>} />
        <Route path="/admin/products" element={<ProtectedRoute requiredRole="admin"><ProductsPage /></ProtectedRoute>} />
        <Route path="/admin/seo-scoring" element={<ProtectedRoute requiredRole="admin"><SEOScoringPage /></ProtectedRoute>} />
        <Route path="/admin/general-analysis-scoring" element={<ProtectedRoute requiredRole="admin"><GeneralAnalysisScoringPage /></ProtectedRoute>} />
        <Route path="/admin/general-settings" element={<ProtectedRoute requiredRole="admin"><GeneralSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/system-services" element={<ProtectedRoute requiredRole="super_admin"><SystemServicesPage /></ProtectedRoute>} />
        
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            border: '1px solid #334155',
          },
        }}
      />
    </Router>
  );
}

export default App;